import externalSelectConfig from '../../Config/externalSelectConfig';

const getComponentByAdmiralQuestionId = (admiralQuestionId, components) => {
  return Object.values(components).find((component) => {
    return component.component?.mappedQuestionId === admiralQuestionId;
  });
};

const isExternalSelect = (component) => {
  return (
    component.component.type === 'select' &&
    Object.keys(externalSelectConfig[process.env.NEXT_PUBLIC_ENV]).indexOf(
      component.component.mappedQuestionId
    ) !== -1
  );
};

const getComponentInputValueByAdmiralQuestionId = (
  admiralQuestionId,
  components
) => {
  return getComponentByAdmiralQuestionId(admiralQuestionId, components)?.input
    ?.value;
};

export const getDependentFieldValuesAndRequestField = ({
  admiralQuestionId,
  components,
  pages,
  environmentExternalSelectDependencies,
}) => {
  const dependentFields =
    environmentExternalSelectDependencies[admiralQuestionId];
  const component = getComponentByAdmiralQuestionId(
    admiralQuestionId,
    components
  );

  if (!component) {
    return [];
  }

  const indexOfCurrentComponent = pages.flat().indexOf(component.component.id);

  const validDependentFields = dependentFields.requiresDataFrom.filter(
    (dependentField) => {
      const dependentComponent = getComponentByAdmiralQuestionId(
        dependentField,
        components
      );
      const indexOfDependentField = pages
        .flat()
        .indexOf(dependentComponent?.component.id);

      if (indexOfDependentField === -1) {
        return false;
      }

      return indexOfDependentField < indexOfCurrentComponent;
    }
  );

  const dependentFieldValuesAndRequestField = validDependentFields.map(
    (questionId) => ({
      name: environmentExternalSelectDependencies[questionId].requestField,
      value: getComponentInputValueByAdmiralQuestionId(questionId, components),
    })
  );

  return dependentFieldValuesAndRequestField;
};

export const isNextSelectToBeAnswered = (componentId, components, pages) => {
  const indexOfCurrentComponent = pages.flat().indexOf(componentId);
  const componentIdsBeforeCurrentComponent = pages
    .flat()
    .slice(0, indexOfCurrentComponent);
  if (componentIdsBeforeCurrentComponent.length === 0) {
    return true;
  }
  // Get component objects that occur before the current component
  const componentsBeforeCurrentComponent =
    componentIdsBeforeCurrentComponent.map((id) => {
      return Object.values(components).find((component) => {
        return component.component.id === id;
      });
    });

  const selectComponentsBeforeCurrentComponent =
    componentsBeforeCurrentComponent.filter(isExternalSelect);

  // Check if the component objects have all been answered
  return selectComponentsBeforeCurrentComponent.every((component) => {
    return (
      component.input?.selectedOptions &&
      component.input?.selectedOptions.length > 0
    );
  });
};

export const isLastSelectOnPage = (componentId, components, pages) => {
  const currentComponent = Object.values(components).find(
    (component) => component.component.id === componentId
  );

  if (isExternalSelect(currentComponent) === false) {
    return false;
  }

  const componentsOnSamePage = pages.find(
    (page) => page.indexOf(componentId) !== -1
  );

  const selectComponentsOnSamePage = componentsOnSamePage.filter(
    (componentIdOnSamePage) => {
      const componentOnSamePage = Object.values(components).find(
        (component) => component.component.id === componentIdOnSamePage
      );
      return isExternalSelect(componentOnSamePage);
    }
  );

  const indexOfCurrentComponent =
    selectComponentsOnSamePage.indexOf(componentId);

  const componentsAfterCurrentComponent = selectComponentsOnSamePage.slice(
    indexOfCurrentComponent + 1
  );
  return componentsAfterCurrentComponent.length === 0;
};

export const getRemainingSelectComponentIds = ({
  componentId,
  components,
  pages,
  environmentExternalSelectDependencies,
}) => {
  const componentsOnSamePage = pages.find(
    (page) => page.indexOf(componentId) !== -1
  );

  const componentsAfterCurrentComponent = componentsOnSamePage.slice(
    componentsOnSamePage.indexOf(componentId) + 1
  );

  const selectComponentsAfterCurrentComponent =
    componentsAfterCurrentComponent.filter((targetComponentId) => {
      const component = Object.values(components).find(
        (targetComponent) => targetComponent.component.id === targetComponentId
      );
      if (!isExternalSelect(component)) {
        return false;
      }
      return (
        Object.keys(environmentExternalSelectDependencies).indexOf(
          component.component?.mappedQuestionId
        ) !== -1
      );
    });

  return selectComponentsAfterCurrentComponent;
};
